/**
 * Parking Hooks
 *
 * CRUD hooks for parking zones and spots.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from './queryKeys';

// Types

export type ZoneType = 'outdoor' | 'indoor' | 'covered';

export interface ParkingZone {
  id: string;
  name: string;
  zoneType: ZoneType;
  totalSpots: number;
  sortOrder: number;
  isActive: boolean;
  notes: string | null;
  spotCount: number;
  availableSpots: number;
  createdAt: string;
  updatedAt: string;
}

export interface ParkingZoneDetail extends ParkingZone {
  spots: ParkingSpot[];
}

export interface ParkingSpot {
  id: string;
  zoneId: string;
  identifier: string;
  isAvailable: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AvailableSpot {
  id: string;
  zoneId: string;
  identifier: string;
  notes: string | null;
  zoneName: string;
  zoneType: ZoneType;
}

interface CreateParkingZoneData {
  name: string;
  zoneType?: ZoneType;
  totalSpots?: number;
  sortOrder?: number;
  isActive?: boolean;
  notes?: string | null;
}

interface UpdateParkingZoneData {
  name?: string;
  zoneType?: ZoneType;
  totalSpots?: number;
  sortOrder?: number;
  isActive?: boolean;
  notes?: string | null;
}

interface CreateParkingSpotData {
  identifier: string;
  notes?: string | null;
}

interface BatchCreateSpotsData {
  prefix: string;
  startNumber: number;
  count: number;
}

// Real-time + utilization types

export interface ParkingSpotStatus {
  spotId: string;
  identifier: string;
  isOccupied: boolean;
  bookingId: string | null;
  vehicleReg: string | null;
}

export interface ParkingZoneStatus {
  zoneId: string;
  zoneName: string;
  zoneType: ZoneType;
  totalSpots: number;
  occupiedCount: number;
  availableCount: number;
  spots: ParkingSpotStatus[];
}

export interface ParkingCurrentStatus {
  zones: ParkingZoneStatus[];
  overall: {
    totalSpots: number;
    occupied: number;
    available: number;
  };
}

export interface ParkingUtilizationZone {
  zoneId: string;
  zoneName: string;
  totalSpots: number;
  totalAvailableMinutes: number;
  totalUsedMinutes: number;
  utilizationPercent: number;
  vehicleCount: number;
  peakOccupancy: number;
  peakTime: string | null;
}

export interface DailyUtilization {
  date: string;
  utilizationPercent: number;
  occupiedAvg: number;
}

export interface ParkingUtilizationReport {
  zones: ParkingUtilizationZone[];
  overall: {
    totalSpots: number;
    totalAvailableMinutes: number;
    totalUsedMinutes: number;
    utilizationPercent: number;
    peakOccupancy: number;
    peakTime: string | null;
  };
  dailyUtilization: DailyUtilization[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// Hooks

export function useParkingCurrentStatus() {
  return useQuery({
    queryKey: queryKeys.parking.current(),
    queryFn: () => api.get<ParkingCurrentStatus>('/parking/current'),
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useParkingUtilization(startDate: string, endDate: string) {
  return useQuery({
    queryKey: queryKeys.parking.utilization(startDate, endDate),
    queryFn: () =>
      api.get<ParkingUtilizationReport>('/parking/utilization', {
        params: { startDate, endDate },
      }),
    enabled: !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
  });
}

export function useParkingZones() {
  return useQuery({
    queryKey: queryKeys.parking.zones.list(),
    queryFn: () => api.get<ParkingZone[]>('/parking/zones'),
    staleTime: 60 * 1000,
  });
}

export function useParkingZone(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.parking.zones.detail(id ?? ''),
    queryFn: () => api.get<ParkingZoneDetail>(`/parking/zones/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useAvailableSpots() {
  return useQuery({
    queryKey: queryKeys.parking.spots.available(),
    queryFn: () => api.get<AvailableSpot[]>('/parking/spots/available'),
    staleTime: 30 * 1000,
  });
}

export function useCreateParkingZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateParkingZoneData) =>
      api.post('/parking/zones', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.zones.all,
      });
    },
  });
}

export function isParkingSpotOccupiedError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'code' in error.data &&
    error.data.code === 'spot_occupied'
  );
}

export function useUpdateParkingZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateParkingZoneData }) =>
      api.patch(`/parking/zones/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.all,
      });
    },
  });
}

export function useDeleteParkingZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/parking/zones/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.zones.all,
      });
    },
  });
}

export function useCreateParkingSpot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ zoneId, data }: { zoneId: string; data: CreateParkingSpotData }) =>
      api.post(`/parking/zones/${zoneId}/spots`, data),
    onSuccess: (_, { zoneId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.zones.detail(zoneId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.zones.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.spots.all,
      });
    },
  });
}

export function useBatchCreateSpots() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ zoneId, data }: { zoneId: string; data: BatchCreateSpotsData }) =>
      api.post(`/parking/zones/${zoneId}/spots/batch`, data),
    onSuccess: (_, { zoneId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.zones.detail(zoneId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.zones.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.spots.all,
      });
    },
  });
}

export function useUpdateParkingSpot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { identifier?: string; isAvailable?: boolean; notes?: string | null } }) =>
      api.patch(`/parking/spots/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.all,
      });
    },
  });
}

export function useDeleteParkingSpot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/parking/spots/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parking.all,
      });
    },
  });
}
